<template>
  <div class="dashboard-tabs-container">
    <div class="tabs-menu" :style="tabsGridStyle">
      <a
        v-for="tab in visibleTabs"
        :key="tab.id"
        class="portal-tab w-inline-block w-tab-link"
        :class="{ 'w--current': activeTab === tab.id }"
        @click.prevent="switchTab(tab.id)"
      >
        <div class="text-block-927">{{ tab.name }}</div>
      </a>
    </div>
    
    <div class="w-tab-content">
      <div
        v-for="tab in visibleTabs"
        :key="tab.id"
        class="w-tab-pane"
        :class="{ 'w--tab-active': activeTab === tab.id }"
        v-show="activeTab === tab.id"
      >
        <component 
          :is="tab.component" 
          :user="user" 
          :baseUrl="baseUrl"
          :key="`${tab.id}-${activeTab}`"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getVisibleTabs, getDefaultTab } from './config/tabConfig';
import myNewSelfSection from './myNewSelfSection.vue';
import ProfileSection from './profileSection.vue';
import OrderInfoSection from './orderInfoSection.vue';
import prescriptionShow from './prescriptionShow.vue';
import talkToDocWindow from './talkToDocWindow.vue';
import buyMoreTir from './buyMoreTir.vue';
import upgradeSema from './upgradeSema.vue';

export default {
  name: 'TabContainer',
  
  components: {
    myNewSelfSection,
    profileSection: ProfileSection,
    orderInfoSection: OrderInfoSection,
    prescriptionShow,
    talkToDocWindow,
    buyMoreTir,
    upgradeSema
  },
  
  props: {
    user: {
      type: Object,
      required: true
    },
    baseUrl: {
      type: String,
      required: true
    },
    computedProps: {
      type: Object,
      required: true
    },
    activeTab: {
      type: String,
      default: null
    }
  },
  
  data() {
    return {
      windowWidth: window.innerWidth
    };
  },
  
  computed: {
    visibleTabs() {
      return getVisibleTabs(this.user, this.computedProps);
    },
    
    defaultTab() {
      return getDefaultTab(this.visibleTabs);
    },
    
    tabsGridStyle() {
      const tabCount = this.visibleTabs.length;
      return {
        'grid-template-columns': `repeat(${tabCount}, 1fr)`,
        'grid-template-rows': this.windowWidth <= 479 ? 'auto auto' : 'auto'
      };
    }
  },
  
  watch: {
    visibleTabs: {
      handler(newTabs) {
        // If current tab is no longer visible, switch to default
        const currentTabVisible = newTabs.some(tab => tab.id === this.activeTab);
        if (!currentTabVisible) {
          this.$emit('tab-change', this.defaultTab);
        }
      },
      immediate: false
    }
  },
  
  mounted() {
    // Set default tab if none is active
    if (!this.activeTab || !this.visibleTabs.some(tab => tab.id === this.activeTab)) {
      this.$emit('tab-change', this.defaultTab);
    }
    
    // Add resize listener for mobile responsiveness
    window.addEventListener('resize', this.handleResize);
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  
  methods: {
    switchTab(tabId) {
      this.$emit('tab-change', tabId);
    },
    
    handleResize() {
      // Update window width for reactivity
      this.windowWidth = window.innerWidth;
    }
  }
};
</script>

<style scoped>
.dashboard-tabs-container {
  width: 100%;
}

.tabs-menu {
  grid-column-gap: 5px;
  grid-row-gap: 5px;
  border-bottom: 1px solid var(--df-grey);
  grid-template-rows: auto;
  grid-auto-columns: 1fr;
  padding-bottom: 20px;
  display: grid;
  align-items: center;
}

@media screen and (max-width: 479px) {
  .tabs-menu {
    grid-column-gap: 5px;
    grid-row-gap: 5px;
    grid-template-rows: auto auto;
    grid-template-columns: 1fr 1fr 1fr !important;
    grid-auto-columns: 1fr;
    display: grid;
    overflow: visible;
  }
}

.portal-tab {
  background-color: var(--transparent);
  font-family: var(--mulish);
  color: var(--df-grey);
  justify-content: center;
  align-items: center;
  padding-left: 0;
  padding-right: 0;
  font-size: 18px;
  font-weight: 900;
  line-height: 24px;
  display: flex;
  cursor: pointer;
  transition: all 0.2s ease;
}

@media screen and (max-width: 479px) {
  .portal-tab {
    border: 1px solid var(--f3-grey);
    text-align: center;
    border-radius: 6px;
    flex-flow: column;
    justify-content: center;
    align-items: center;
    height: 50px;
    padding-top: 5px;
    padding-bottom: 5px;
    padding-right: 0;
    display: flex;
  }
}

.portal-tab.w--current {
  background-color: var(--transparent);
  font-family: var(--mulish);
  color: var(--black);
  flex-flow: column;
  justify-content: center;
  align-items: center;
  padding-right: 0;
  font-size: 18px;
  font-weight: 900;
  line-height: 24px;
  display: flex;
}

@media screen and (max-width: 479px) {
  .portal-tab.w--current {
    border-color: var(--pastel-blue);
    display: flex;
  }
}

.w-tab-pane {
  opacity: 0;
  transition: opacity 300ms ease;
  min-height: 400px;
}

.w-tab-pane.w--tab-active {
  opacity: 1;
  pointer-events: auto;
}
</style>